import { z } from 'zod';

import {
    responsiveBooleanSchema,
    responsiveNullableStringSchema,
    responsiveNumberSchema,
    responsiveSchema,
    responsiveStringSchema,
} from '../../utils';
import { selectedFileSchemaLegacy } from '../../widget/common/schemas/legacy';
import { responsiveColorValueSchema } from '../../mixins';

/**
 * The picked file, plus the resize flag the host adds on top of the shared file
 * shape. It travels whole: the CSS builders need its resize flags and the host's
 * CDN base, so an id would only move that lookup around.
 */
export const backgroundFileSchema = selectedFileSchemaLegacy.extend({ hasResize: z.boolean().nullish() });

/**
 * `isColorOverflow` / `colorOverflow` / `colorOverflowOpacity` — a tint over the
 * image. Like the filter and the scroll effect below, it keeps only its switch
 * while it is off at every breakpoint; switching it on seeds the values.
 */
export const backgroundOverlaySchema = z.object({
    isEnabled: responsiveBooleanSchema,
    color: responsiveStringSchema.optional(),
    opacity: responsiveNumberSchema.optional(),
});

/** `isFilterOverlay` / `filterOverlay` — a CSS filter literal, not a symbolic name. */
export const backgroundFilterSchema = z.object({
    isEnabled: responsiveBooleanSchema,
    value: responsiveStringSchema.optional(),
});

/** `isScrollEffect` / `scrollEffect` — the value is an `EScrollEffect` literal. */
export const backgroundScrollEffectSchema = z.object({
    isEnabled: responsiveBooleanSchema,
    value: responsiveStringSchema.optional(),
});

export const backgroundV0Schema = z.object({
    version: z.literal(0),
    color: responsiveColorValueSchema,
    image: responsiveSchema(backgroundFileSchema.nullable()),
    /** `null` leaves the CSS default — legacy wrote an empty string for it. */
    size: responsiveNullableStringSchema,
    position: responsiveNullableStringSchema,
    repeat: responsiveNullableStringSchema,
    overlay: backgroundOverlaySchema,
    filter: backgroundFilterSchema,
    /**
     * The block's two extras. A column paints no video, and its scroll effect is
     * the flat `scrollEffect` feature rather than this per-breakpoint pair — see
     * `features/scroll-effect` — so both are absent on a column.
     */
    video: responsiveSchema(backgroundFileSchema.nullable()).optional(),
    scrollEffect: backgroundScrollEffectSchema.optional(),
});
